import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

import { RootState } from '../../redux/root-reducer';
import { endpoints } from '../../utils/endpoints';
import { fetchJSON } from '../../utils/fetch';

const get = endpoints();

export interface SessionsState {
  ticketsWithSessions: Record<string, boolean>;
  requestState: 'idle' | 'loading' | 'completed';
  expireTime: number | null;
  items?: any[]; // See ScheduleItemV2 in '@hopin-team/api-hopin'
}

export const postStartTimer = async (eventSlug: string, orderSlug: string) => {
  return fetchJSON(get('START_TIMER')(eventSlug, orderSlug), {
    method: 'POST',
  });
};

export const fetchSchedules = (eventSlug: string, ticketExternalId: string) => {
  return fetchJSON(
    `${get('SESSIONS_FOR_TICKET')(eventSlug, ticketExternalId)}`,
    {
      method: 'GET',
    },
  );
};

export const startTimer = createAsyncThunk(
  'sessions/startTimer',
  async ({ eventSlug, orderSlug }: { eventSlug: string; orderSlug: string }) =>
    await postStartTimer(eventSlug, orderSlug),
);

export const fetchSessionsByTicketId = createAsyncThunk(
  'sessions/fetchSessionsByTicketId',
  async ({
    eventSlug,
    ticketExternalId,
  }: {
    eventSlug: string;
    ticketExternalId: string;
  }) => await fetchSchedules(eventSlug, ticketExternalId),
);

export const resetExpireTime = createAction('resetExpireTime');

export const initialState: SessionsState = {
  ticketsWithSessions: {},
  requestState: 'idle',
  expireTime: null,
  items: [],
};

const slice = createSlice({
  name: 'sessions',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSessionsByTicketId.pending, state => {
      state.requestState = 'loading';
    });
    builder.addCase(fetchSessionsByTicketId.fulfilled, (state, action) => {
      state.items = action.payload.items;
      state.ticketsWithSessions[action.meta.arg.ticketExternalId] =
        action.payload.items.length > 0;
      state.requestState = 'idle';
    });

    builder.addCase(fetchSessionsByTicketId.rejected, (state, action) => {
      state.ticketsWithSessions[action.meta.arg.ticketExternalId] = false;
      state.requestState = 'idle';
    });

    builder.addCase(startTimer.fulfilled, (state, action) => {
      state.expireTime = Date.now() + action.payload.time_left;
    });

    builder.addCase(resetExpireTime, state => {
      state.expireTime = null;
    });
  },
});

export const selectSessions = createSelector(
  (state: RootState) => state.sessions,
  sessions => sessions.ticketsWithSessions,
);

export const selectSessionsRequestState = createSelector(
  (state: RootState) => state.sessions,
  sessions => sessions.requestState,
);

export const selectSessionsExpireTime = createSelector(
  (state: RootState) => state.sessions,
  sessions => sessions.expireTime,
);

export const getSessionsForTicket = createSelector(
  (state: RootState) => state.sessions,
  sessions => sessions.items,
);

export const getIsSessionCapacityAvailable = createSelector(
  (state: RootState) => state.sessions,
  sessions => !!sessions.items?.some(item => item.capacity > 0),
);

export const { reducer } = slice;
